
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useFetch } from '@/hooks/useFetch';
import { getOrders } from '@/api/orders';
import { ORDER_STATUS_CONFIG } from '@/types/order';
import type { Order, OrderStatus } from '@/types/order';
import OrderCard from './components/OrderCard';
import OrderDetailModal from './components/OrderDetailModal';
import { event as gaEvent } from '@/lib/gtag';

const statusFilters: { key: 'all' | OrderStatus; label: string }[] = [
  { key: 'all', label: '전체' },
  { key: 'pending', label: '주문대기' },
  { key: 'paid', label: '결제완료' },
  { key: 'processing', label: '제작중' },
  { key: 'completed', label: '완료' },
  { key: 'cancelled', label: '취소' },
];

export default function MyOrdersPage() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [selectedFilter, setSelectedFilter] = useState<'all' | OrderStatus>('all');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  const { data: orders, loading, refetch } = useFetch(getOrders);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#FDF5EA] to-white">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-[#C9A96E] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-gray-400">불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  const orderList = orders || [];

  const filteredOrders = selectedFilter === 'all'
    ? orderList
    : orderList.filter(o => o.status === selectedFilter);

  const handleLogout = async () => {
    gaEvent('logout', { location: 'my_orders' });
    await logout();
    navigate('/');
  };

  const stats = {
    total: orderList.length,
    inProgress: orderList.filter(o => o.status === 'processing').length,
    completed: orderList.filter(o => o.status === 'completed').length,
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDF5EA] to-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <a
            href="/"
            className="flex items-center gap-2 cursor-pointer"
          >
            <span className="font-serif text-2xl font-medium tracking-tight text-[#2D2D2D]">봄날</span>
            <span className="text-xs tracking-[0.2em] font-light text-[#C9A96E]">AI WEDDING</span>
          </a>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#FDF5EA]">
                <i className="ri-user-line text-[#C9A96E] text-sm"></i>
              </div>
              <span className="text-sm text-gray-700 font-medium">{user?.name}</span>
            </div>
            <button
              onClick={handleLogout}
              className="text-sm text-gray-400 hover:text-gray-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
            >
              <i className="ri-logout-box-r-line"></i>
              로그아웃
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Page Title */}
        <div className="mb-10">
          <h1 className="font-serif text-3xl font-light text-[#2D2D2D] mb-2">내 주문 내역</h1>
          <p className="text-sm text-gray-400">주문하신 AI 웨딩 사진의 진행 상황을 확인하세요</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-10">
          <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm text-center">
            <p className="text-3xl font-bold text-[#2D2D2D] mb-1">{stats.total}</p>
            <p className="text-xs text-gray-400 font-medium">전체 주문</p>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm text-center">
            <p className="text-3xl font-bold text-blue-500 mb-1">{stats.inProgress}</p>
            <p className="text-xs text-gray-400 font-medium">작업중</p>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm text-center">
            <p className="text-3xl font-bold text-green-500 mb-1">{stats.completed}</p>
            <p className="text-xs text-gray-400 font-medium">완료</p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center gap-2 mb-6 bg-white rounded-full p-1 border border-gray-100 shadow-sm w-full overflow-x-auto scrollbar-hide">
          {statusFilters.map(f => (
            <button
              key={f.key}
              onClick={() => setSelectedFilter(f.key)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                selectedFilter === f.key
                  ? 'bg-[#2D2D2D] text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {f.label}
              {f.key !== 'all' && (
                <span className={`ml-1.5 text-xs ${selectedFilter === f.key ? 'text-white/70' : 'text-gray-400'}`}>
                  {orderList.filter(o => o.status === f.key).length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Order List */}
        {filteredOrders.length > 0 ? (
          <div className="space-y-4">
            {filteredOrders.map(order => (
              <OrderCard
                key={order.orderId}
                order={order}
                onViewDetail={setSelectedOrder}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-50 mx-auto mb-4">
              <i className="ri-inbox-line text-3xl text-gray-300"></i>
            </div>
            <p className="text-gray-400 text-sm mb-6">해당 상태의 주문이 없어요</p>
            <button
              onClick={() => { gaEvent('click_cta', { location: 'my_orders_empty', button: 'order_now' }); navigate('/order'); }}
              className="px-8 py-3.5 bg-[#C9A96E] text-white text-sm font-medium rounded-full hover:bg-[#b8935a] transition-all duration-300 cursor-pointer shadow-lg hover:shadow-xl"
            >
              지금 주문하기
            </button>
          </div>
        )}

        {/* New Order CTA */}
        <div className="mt-12 bg-gradient-to-r from-[#2D2D2D] to-[#4a4a4a] rounded-2xl p-8 flex items-center justify-between">
          <div>
            <p className="text-white font-medium text-lg mb-1">새로운 웨딩 사진을 만들어보세요</p>
            <p className="text-white/60 text-sm">AI가 만드는 나만의 특별한 웨딩 사진</p>
          </div>
          <button
            onClick={() => { gaEvent('click_cta', { location: 'my_orders_banner', button: 'order' }); navigate('/order'); }}
            className="px-6 py-3 bg-[#C9A96E] text-white text-sm font-medium rounded-full hover:bg-[#b8935a] transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg"
          >
            주문하기
          </button>
        </div>
      </main>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <OrderDetailModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
        />
      )}
    </div>
  );
}
